import { useState } from 'react';

import { getOrCreateGlobalStore } from '@adsk/alloy-react-context';

// Zustand's setState synchronously notifies useSyncExternalStore subscribers,
// causing React's to fire a DEV-only console.error ("Cannot update
// a component while rendering a different component") when we write to the
// store during render. This is a false positive for external stores — the
// behavior is correct and warning-free in production. We silence only this
// specific error so the global test setup still catches real React bugs.
const withSuppressedSetStateInRenderWarning = (fn: () => void) => {
  if (process.env.NODE_ENV === 'production') {
    fn();
    return;
  }
  const original = console.error;
  console.error = (...args: Parameters<typeof console.error>) => {
    if (
      typeof args[0] !== 'string' ||
      // React 18/19: URL is the final token. React 17: printWarning appends
      // a component-stack placeholder ("%s") after the URL when a stack is
      // available, so the string ends with the URL or "URL%s".
      (!/https:\/\/reactjs\.org\/link\/setstate-in-render(?:%s)?$/.test(
        args[0],
      ) &&
        !/https:\/\/react\.dev\/link\/setstate-in-render(?:%s)?$/.test(args[0]))
    ) {
      original(...args);
    }
  };
  try {
    fn();
  } finally {
    console.error = original;
  }
};

type SetterName<K extends string> = `set${Capitalize<K>}`;

export type CurrentIdStore<K extends string, V> = { [P in K]?: V } & {
  [S in SetterName<K>]: (value?: V) => void;
};

// The store is created with computed property names from a generic K, so
// TypeScript cannot statically prove the field/setter exist on the store
// object. These aliases narrow the unsafe indexed access to one place.
type StoreReader<V> = Record<string, V | undefined>;
type StoreWriter<V> = Record<string, (value?: V) => void>;

/**
 * Creates a global Zustand store hook for a single current-id value.
 *
 * The returned hook accepts an optional `prop` and **synchronously** writes it
 * to the store during render whenever it changes — so all consumers, including
 * those in separate React trees (Module Federation), see the updated value
 * before any `useEffect` runs. The setter is a no-op when the value is equal
 * to what is already in the store.
 *
 * Note: the synchronous store write during render triggers a DEV-only
 * console.error ("Cannot update a component while rendering a different
 * component"). This is a false positive for external Zustand stores — the
 * warning only applies to React state. The behavior is correct and
 * warning-free in production.
 *
 * @example
 * export const useCurrentToolIdStore = createCurrentIdStore<'toolId', ToolIdType>({
 *   contextName: CONTEXT_NAMES.CurrentToolIdContext,
 *   fieldName: 'toolId',
 * });
 */
export const createCurrentIdStore = <K extends string, V = string>({
  contextName,
  fieldName,
}: {
  contextName: string;
  fieldName: K;
}) => {
  const setterName = `set${
    fieldName.charAt(0).toUpperCase() + fieldName.slice(1)
  }` as SetterName<K>;

  // getOrCreateGlobalStore returns a hook at run time, which React Compiler
  // cannot see. Without this directive it would memoize the call and skip the
  // hook on re-render, changing the hook order.
  function useCurrentIdStore(prop?: V): CurrentIdStore<K, V> {
    'use no memo';

    const store = getOrCreateGlobalStore<CurrentIdStore<K, V>>(
      (set, get) =>
        ({
          [fieldName]: prop,
          [setterName]: (next?: V) => {
            if (next !== (get() as StoreReader<V>)[fieldName]) {
              set({ [fieldName]: next } as Partial<CurrentIdStore<K, V>>);
            }
          },
        }) as CurrentIdStore<K, V>,
      contextName,
    )();

    // The setter already no-ops when next === current (see store factory
    // above), so we can call it unconditionally and let it short-circuit.
    const syncStore = () => {
      withSuppressedSetStateInRenderWarning(() => {
        (store as StoreWriter<V>)[setterName](prop);
      });
    };

    // Lazy init: runs once per mount. Handles the remount case where
    // getOrCreateGlobalStore is a singleton — the store retains the value from
    // a previous mount, so we must sync on the very first render too.
    const [prev, setPrev] = useState<V | undefined>(() => {
      syncStore();
      return prop;
    });

    // Subsequent renders: prop changed while the provider is still mounted.
    if (prev !== prop) {
      setPrev(prop);
      syncStore();
    }

    return store;
  }

  return useCurrentIdStore;
};
